import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Chart, Line } from 'react-chartjs-2'
import { FiAlertCircle } from 'react-icons/fi'

import { Button } from '@vtex/shoreline'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  ChartOptions,
  ChartDataset
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { t } from 'i18next'

import ChartLegend, { type ChartLegendItem } from '../ChartLegend'
import crosshairPlugin from '../plugins/crosshairPlugin'
import { makePillPlugin } from '../plugins/makePillPlugin'
import {
  SpendTooltip,
  useSpendTooltip,
  type TooltipItem
} from '../SpendTooltip'

import styles from './styles.module.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  annotationPlugin,
  crosshairPlugin
)

export interface SpendBarChartProps {
  /** X-axis labels (dates like "13 abr." or hours like "0h") */
  labels?: string[]
  /** Spend consumed up to goal */
  consumedData?: (number | null)[]
  /** Remaining daily budget (unfilled portion) */
  remainingData?: (number | null)[]
  /** Spend above goal */
  aboveGoalData?: (number | null)[]
  /** Impression Share percentage points */
  impressionShareData?: (number | null)[]
  /** Lost Impression Share by rank percentage points */
  lostIsRankData?: (number | null)[]
  /** Enables Impression Share line on daily charts */
  showImpressionShare?: boolean
  /** Index of current day/hour in labels array */
  elapsedIndex?: number
  /** Label of the current-moment pill ("Hoje" or "Agora") */
  pillLabel?: string
  /** Index of campaign end date in labels array (-1 or undefined = not shown) */
  endIndex?: number
  /** Label for the campaign end vertical line */
  endLabel?: string
  /** Whether to use hourly tick settings (maxTicksLimit: 12) */
  hourly?: boolean
  /** Goal constant for tooltip (daily_budget or hourly_goal from API root) */
  goalValue?: number
  /** ISO 4217 currency code from the API response */
  currency?: string
  /** Show error state instead of chart */
  error?: boolean
  /** Expected-state message shown instead of the generic error (no retry button) */
  errorMessage?: string
  /** Called when user clicks "Tentar novamente" */
  onRetry?: () => void
}

const MIN_POINT_WIDTH = 50
const Y_AXIS_WIDTH = 80
const CHART_HEIGHT = 280
const IMPRESSION_SHARE_COLOR = '#059669'
const LOST_IS_RANK_COLOR = '#d97706'

const LEGEND_ITEMS_DAILY: ChartLegendItem[] = [
  { key: 'consumed', label: 'Gasto diário', type: 'square', color: '#0366dd' },
  {
    key: 'remaining',
    label: 'Orçamento médio diário',
    type: 'square',
    color: '#eaeaea',
    border: true
  },
  { key: 'aboveGoal', label: 'Acima da meta', type: 'square', color: '#034a9e' }
]

const LEGEND_ITEMS_HOURLY: ChartLegendItem[] = [
  { key: 'consumed', label: 'Gasto diário', type: 'square', color: '#0366dd' },
  {
    key: 'remaining',
    label: 'Meta por hora',
    type: 'square',
    color: '#eaeaea',
    border: true
  },
  { key: 'aboveGoal', label: 'Acima da meta', type: 'square', color: '#034a9e' }
]

const IMPRESSION_SHARE_LEGEND_ITEM: ChartLegendItem = {
  key: 'impressionShare',
  label: 'Impression Share',
  color: IMPRESSION_SHARE_COLOR
}

const LOST_IS_RANK_LEGEND_ITEM: ChartLegendItem = {
  key: 'lostIsRank',
  label: 'Lost Impression Share (by rank)',
  color: LOST_IS_RANK_COLOR
}

const SpendBarChart: React.FC<SpendBarChartProps> = ({
  labels = [],
  consumedData = [],
  remainingData = [],
  aboveGoalData = [],
  impressionShareData = [],
  lostIsRankData = [],
  showImpressionShare = false,
  elapsedIndex = -1,
  pillLabel = 'Hoje',
  hourly = false,
  goalValue = 0,
  currency = 'BRL',
  endIndex = -1,
  endLabel = '',
  error = false,
  errorMessage,
  onRetry
}) => {
  const showImpressionShareLine = showImpressionShare && !hourly
  const baseLegendItems = hourly ? LEGEND_ITEMS_HOURLY : LEGEND_ITEMS_DAILY
  const LEGEND_ITEMS = [...baseLegendItems]
  if (showImpressionShareLine) {
    LEGEND_ITEMS.push(IMPRESSION_SHARE_LEGEND_ITEM, LOST_IS_RANK_LEGEND_ITEM)
  }

  const id = useId().replaceAll(':', '')
  const chartRef = useRef<ChartJS<'bar'>>(null)
  const mainScrollRef = useRef<HTMLDivElement>(null)
  const xAxisScrollRef = useRef<HTMLDivElement>(null)
  const scrollWrapperRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef({ active: false, startX: 0, startScrollLeft: 0 })
  const elapsedIndexRef = useRef(elapsedIndex)
  useLayoutEffect(() => {
    elapsedIndexRef.current = elapsedIndex
  })

  // Ref holding raw tooltip values (always original API values regardless of legend toggle)
  const getItemsRef = useRef<((index: number) => TooltipItem[]) | undefined>(
    undefined
  )
  getItemsRef.current = (index: number): TooltipItem[] => {
    const ITEMS = hourly ? LEGEND_ITEMS_HOURLY : LEGEND_ITEMS_DAILY
    const c = consumedData[index] ?? 0
    const a = aboveGoalData[index] ?? 0
    const isNullDay =
      consumedData[index] === null && remainingData[index] === null
    const items: TooltipItem[] = [
      {
        datasetLabel: ITEMS[0].label,
        value: isNullDay ? null : c + a,
        color: ITEMS[0].color
      },
      {
        datasetLabel: ITEMS[1].label,
        value: isNullDay ? null : goalValue,
        color: ITEMS[1].color
      },
      {
        datasetLabel: ITEMS[2].label,
        value: isNullDay ? null : a,
        color: ITEMS[2].color
      }
    ]

    const impressionShareValue = impressionShareData[index]
    if (showImpressionShareLine && impressionShareValue != null) {
      items.push({
        datasetLabel: IMPRESSION_SHARE_LEGEND_ITEM.label,
        value: impressionShareValue,
        color: IMPRESSION_SHARE_COLOR,
        format: 'percent'
      })
    }

    const lostIsRankValue = lostIsRankData[index]
    if (showImpressionShareLine && lostIsRankValue != null) {
      items.push({
        datasetLabel: LOST_IS_RANK_LEGEND_ITEM.label,
        value: lostIsRankValue,
        color: LOST_IS_RANK_COLOR,
        format: 'percent'
      })
    }

    return items
  }

  const { tooltipData, position, tooltipRef, externalTooltipHandler } =
    useSpendTooltip(getItemsRef)

  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set())

  const toggleLegend = useCallback((key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const syncShadows = useCallback((el: HTMLDivElement) => {
    const w = scrollWrapperRef.current
    if (!w) return
    const hasScroll = el.scrollWidth > el.clientWidth
    el.classList.toggle(styles.scrollable, hasScroll)
    w.classList.toggle(styles.shadowLeft, el.scrollLeft > 0)
    w.classList.toggle(
      styles.shadowRight,
      hasScroll && el.scrollLeft < el.scrollWidth - el.clientWidth - 1
    )
  }, [])

  // Sync x-axis strip when main chart scrolls
  const handleMainScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (xAxisScrollRef.current) {
        xAxisScrollRef.current.scrollLeft = e.currentTarget.scrollLeft
      }
      syncShadows(e.currentTarget)
    },
    [syncShadows]
  )

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = mainScrollRef.current
    if (!el) return
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft
    }
    el.classList.add(styles.dragging)
  }, [])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!dragRef.current.active) return
      e.preventDefault()
      const el = mainScrollRef.current
      if (!el) return
      el.scrollLeft =
        dragRef.current.startScrollLeft - (e.clientX - dragRef.current.startX)
      if (xAxisScrollRef.current)
        xAxisScrollRef.current.scrollLeft = el.scrollLeft
      syncShadows(el)
    },
    [syncShadows]
  )

  const handleMouseUpOrLeave = useCallback(() => {
    dragRef.current.active = false
    mainScrollRef.current?.classList.remove(styles.dragging)
  }, [])

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      const el = mainScrollRef.current
      if (!el) return
      dragRef.current = {
        active: true,
        startX: e.touches[0].clientX,
        startScrollLeft: el.scrollLeft
      }
    },
    []
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!dragRef.current.active) return
      const el = mainScrollRef.current
      if (!el) return
      el.scrollLeft =
        dragRef.current.startScrollLeft -
        (e.touches[0].clientX - dragRef.current.startX)
      if (xAxisScrollRef.current)
        xAxisScrollRef.current.scrollLeft = el.scrollLeft
      syncShadows(el)
    },
    [syncShadows]
  )

  const handleTouchEnd = useCallback(() => {
    dragRef.current.active = false
  }, [])

  // Scroll to end (today) on load; sync shadows
  useEffect(() => {
    requestAnimationFrame(() => {
      const el = mainScrollRef.current
      if (!el) return
      if (elapsedIndexRef.current > 0) {
        el.scrollLeft = el.scrollWidth
        if (xAxisScrollRef.current)
          xAxisScrollRef.current.scrollLeft = el.scrollLeft
      }
      syncShadows(el)
    })
  }, [labels, syncShadows])

  // Shared Y-axis ceiling: max stacked total across all data points
  const yMax = useMemo(() => {
    let max = 0
    for (let i = 0; i < consumedData.length; i++) {
      const total =
        (consumedData[i] ?? 0) +
        (remainingData[i] ?? 0) +
        (aboveGoalData[i] ?? 0)
      if (total > max) max = total
    }
    return max
  }, [consumedData, remainingData, aboveGoalData])

  // Pill plugin lives on x-axis strip (not on main chart)
  const xAxisPlugins = useMemo(() => {
    const plugins = [
      makePillPlugin(id, pillLabel, () => elapsedIndexRef.current)
    ]
    if (endIndex >= 0 && endLabel) {
      plugins.push(makePillPlugin(`end-${id}`, endLabel, () => endIndex, true))
    }
    return plugins
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pillLabel, endIndex, endLabel])

  // Invisible dataset to drive the fixed Y-axis scale (stacked sums)
  const yAxisDatasets: ChartDataset<'line'>[] = useMemo(
    () => [
      {
        label: '_scale',
        data: labels.map(
          (_, i) =>
            (consumedData[i] ?? 0) +
            (remainingData[i] ?? 0) +
            (aboveGoalData[i] ?? 0)
        ),
        borderWidth: 0,
        pointRadius: 0,
        borderColor: 'transparent' as const,
        backgroundColor: 'transparent' as const
      }
    ],
    [labels, consumedData, remainingData, aboveGoalData]
  )

  // Invisible dataset to give x-axis strip the same x range
  const xAxisDatasets: ChartDataset<'line'>[] = useMemo(
    () => [
      {
        label: '_x',
        data: labels.map(() => 0),
        borderWidth: 0,
        pointRadius: 0,
        borderColor: 'transparent' as const,
        backgroundColor: 'transparent' as const
      }
    ],
    [labels]
  )

  const datasets: ChartDataset<'bar' | 'line'>[] = useMemo(() => {
    const ITEMS = hourly ? LEGEND_ITEMS_HOURLY : LEGEND_ITEMS_DAILY
    const consumedHidden = hiddenKeys.has('consumed')
    const remainingHidden = hiddenKeys.has('remaining')
    const aboveGoalHidden = hiddenKeys.has('aboveGoal')
    const impressionShareHidden = hiddenKeys.has('impressionShare')
    const lostIsRankHidden = hiddenKeys.has('lostIsRank')

    const visibleConsumed: (number | null)[] = consumedHidden
      ? consumedData.map((): number | null => null)
      : consumedData

    // When consumed is hidden, remaining expands to full meta (consumed + remaining)
    let visibleRemaining: (number | null)[]
    if (remainingHidden) {
      visibleRemaining = remainingData.map((): number | null => null)
    } else if (consumedHidden) {
      visibleRemaining = consumedData.map((c, i): number | null => {
        const r = remainingData[i]
        if (c === null && r === null) return null
        return (c ?? 0) + (r ?? 0)
      })
    } else {
      visibleRemaining = remainingData
    }

    const visibleAboveGoal: (number | null)[] = aboveGoalHidden
      ? aboveGoalData.map((): number | null => null)
      : aboveGoalData

    const visibleImpressionShare: (number | null)[] = impressionShareHidden
      ? impressionShareData.map((): number | null => null)
      : impressionShareData

    const visibleLostIsRank: (number | null)[] = lostIsRankHidden
      ? lostIsRankData.map((): number | null => null)
      : lostIsRankData

    const chartDatasets: ChartDataset<'bar' | 'line'>[] = [
      {
        type: 'bar' as const,
        label: ITEMS[0].label,
        data: visibleConsumed,
        backgroundColor: ITEMS[0].color,
        borderRadius: { bottomLeft: 2, bottomRight: 2 },
        borderSkipped: false,
        stack: 'dailyProgress',
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        maxBarThickness: 70,
        order: 1
      },
      {
        type: 'bar' as const,
        label: ITEMS[1].label,
        data: visibleRemaining,
        backgroundColor: ITEMS[1].color,
        borderRadius: 0,
        borderSkipped: false,
        stack: 'dailyProgress',
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        maxBarThickness: 70,
        order: 2
      },
      {
        type: 'bar' as const,
        label: ITEMS[2].label,
        data: visibleAboveGoal,
        backgroundColor: ITEMS[2].color,
        borderRadius: { topLeft: 2, topRight: 2 },
        borderSkipped: false,
        stack: 'dailyProgress',
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        maxBarThickness: 70,
        order: 3
      }
    ]

    if (showImpressionShareLine) {
      chartDatasets.push(
        {
          type: 'line' as const,
          label: IMPRESSION_SHARE_LEGEND_ITEM.label,
          data: visibleImpressionShare,
          borderColor: IMPRESSION_SHARE_COLOR,
          backgroundColor: '#ffffff',
          borderWidth: 2,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          pointBorderColor: IMPRESSION_SHARE_COLOR,
          pointBackgroundColor: '#ffffff',
          tension: 0.35,
          fill: false,
          yAxisID: 'impressionShare',
          order: 0
        },
        {
          type: 'line' as const,
          label: LOST_IS_RANK_LEGEND_ITEM.label,
          data: visibleLostIsRank,
          borderColor: LOST_IS_RANK_COLOR,
          backgroundColor: '#ffffff',
          borderWidth: 2,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          pointBorderColor: LOST_IS_RANK_COLOR,
          pointBackgroundColor: '#ffffff',
          tension: 0.35,
          fill: false,
          yAxisID: 'impressionShare',
          order: 0
        }
      )
    }

    return chartDatasets
  }, [
    consumedData,
    remainingData,
    aboveGoalData,
    impressionShareData,
    lostIsRankData,
    hiddenKeys,
    hourly,
    showImpressionShareLine
  ])

  const showAnnotation =
    elapsedIndex >= 0 && (!hourly || elapsedIndex < labels.length - 1)

  // Fixed Y-axis panel: no x-axis, just the scale
  const yAxisOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        annotation: { annotations: {} }
      },
      layout: { padding: 0 },
      scales: {
        x: { display: false },
        y: {
          min: 0,
          suggestedMax: yMax,
          grid: { color: '#f2f2f2' },
          border: { display: false },
          ticks: {
            count: 5,
            callback: v =>
              new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency,
                maximumFractionDigits: 0
              }).format(v as number),
            font: { size: 12 },
            color: '#707070',
            padding: 8
          },
          afterFit(scale) {
            scale.width = Y_AXIS_WIDTH
          }
        }
      }
    }),
    [yMax, currency]
  )

  // Main chart: x and y axes hidden (y-axis panel + x-axis strip take over)
  const options: ChartOptions<'bar'> = useMemo(() => {
    const annotations = {}
    if (showAnnotation) {
      Object.assign(annotations, {
        nowLine: {
          type: 'line',
          xMin: elapsedIndex,
          xMax: elapsedIndex,
          borderColor: '#707070',
          borderWidth: 1,
          borderDash: [2, 2]
        }
      })
    }
    if (endIndex >= 0) {
      Object.assign(annotations, {
        endLine: {
          type: 'line',
          xMin: endIndex,
          xMax: endIndex,
          borderColor: '#707070',
          borderWidth: 1,
          borderDash: [2, 2]
        }
      })
    }

    const scales: NonNullable<ChartOptions<'bar'>['scales']> = {
      x: {
        display: false,
        stacked: true
      },
      y: {
        stacked: true,
        min: 0,
        suggestedMax: yMax,
        grid: { color: '#f2f2f2' },
        border: { display: false },
        ticks: { display: false },
        afterFit(scale) {
          scale.width = 0
        }
      }
    }

    const hasLostIsRankData = lostIsRankData.some(value => value != null)
    if (
      showImpressionShareLine &&
      (!hiddenKeys.has('impressionShare') ||
        (hasLostIsRankData && !hiddenKeys.has('lostIsRank')))
    ) {
      Object.assign(scales, {
        impressionShare: {
          display: true,
          position: 'right' as const,
          min: 0,
          max: 100,
          grid: {
            drawOnChartArea: false,
            drawTicks: true,
            tickLength: 8,
            color: '#d1d5db'
          },
          border: { display: false },
          ticks: {
            display: true,
            stepSize: 10,
            callback: (value: string | number) => `${value}%`,
            color: '#707070',
            font: { size: 12 },
            padding: 8
          }
        }
      })
    }

    return {
      indexAxis: 'x',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 0, right: 20, bottom: 0 } },
      interaction: { mode: 'index', intersect: false },
      animation: { duration: 700, easing: 'easeOutQuart' },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler as never
        },
        annotation: { annotations }
      },
      scales
    }
  }, [
    elapsedIndex,
    showAnnotation,
    externalTooltipHandler,
    yMax,
    endIndex,
    showImpressionShareLine,
    lostIsRankData,
    hiddenKeys
  ])

  // X-axis strip: only x-axis visible, pill plugin, scroll synced to main
  const xAxisOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { top: 0, right: 20, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        annotation: { annotations: {} }
      },
      scales: {
        x: {
          offset: true, // match bar chart category offset
          grid: { display: false },
          border: { display: false },
          afterFit(scale) {
            scale.height = 40
          },
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            callback(_value, index) {
              if (hourly) return index % 2 === 0 ? labels[index] : ''
              if (index === 0) return elapsedIndex === 0 ? '' : labels[0]
              return ''
            },
            font: { size: hourly ? 11 : 12 },
            color: '#707070',
            padding: hourly ? 8 : 12
          }
        },
        y: {
          display: false,
          afterFit(scale) {
            scale.width = 0
          }
        }
      }
    }),
    [labels, hourly, elapsedIndex]
  )

  const chartMinWidth = useMemo(
    () => labels.length * MIN_POINT_WIDTH,
    [labels.length]
  )

  return (
    <div className={styles.box}>
      {error ? (
        <div className={styles.errorArea}>
          <FiAlertCircle size={32} color="#707070" />
          <span>{errorMessage ?? 'Não foi possível carregar o gráfico'}</span>
          {!errorMessage && onRetry && (
            <Button onClick={onRetry}>Tentar novamente</Button>
          )}
        </div>
      ) : (
        <>
          {/* Chart area row: fixed Y-axis + scrollable bars */}
          <div className={styles.scrollWrapper}>
            <div className={styles.yAxisPanel}>
              <Line
                data={{ labels, datasets: yAxisDatasets }}
                options={yAxisOptions}
                width={Y_AXIS_WIDTH}
                height={CHART_HEIGHT}
              />
            </div>
            <div ref={scrollWrapperRef} className={styles.chartScrollWrapper}>
              <div // NOSONAR
                ref={mainScrollRef}
                role="application"
                aria-label={t('rm:spendReport.chart.ariaLabel')}
                tabIndex={0} // NOSONAR
                className={styles.chartScrollArea}
                onScroll={handleMainScroll}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUpOrLeave}
                onMouseLeave={handleMouseUpOrLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onKeyDown={e => {
                  const el = mainScrollRef.current
                  if (!el) return
                  if (e.key === 'ArrowLeft') el.scrollLeft -= MIN_POINT_WIDTH
                  else if (e.key === 'ArrowRight')
                    el.scrollLeft += MIN_POINT_WIDTH
                }}
              >
                <div
                  className={styles.chartScrollInner}
                  style={{ minWidth: chartMinWidth }}
                >
                  <Chart
                    ref={chartRef as never}
                    type="bar"
                    data={{
                      labels,
                      datasets: datasets as ChartDataset<'bar'>[]
                    }}
                    options={options}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* X-axis row: spacer aligned to Y-axis + scrollable x-axis strip (no scrollbar) */}
          <div className={styles.xAxisRow}>
            <div className={styles.xAxisSpacer} />
            <div ref={xAxisScrollRef} className={styles.xAxisScrollArea}>
              <div
                className={styles.xAxisScrollInner}
                style={{ minWidth: chartMinWidth }}
              >
                <Line
                  data={{ labels, datasets: xAxisDatasets }}
                  options={xAxisOptions}
                  plugins={xAxisPlugins as never}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <ChartLegend
        items={LEGEND_ITEMS}
        hiddenKeys={hiddenKeys}
        onToggle={toggleLegend}
        className={styles.legend}
      />

      <SpendTooltip
        data={tooltipData}
        position={position}
        tooltipRef={tooltipRef}
        currency={currency}
      />
    </div>
  )
}

export default SpendBarChart
